
import React, { useState, useEffect, useMemo } from "react";
// import "./VendorCare.css";
import { Link } from "react-router-dom";

const STATUS_OPTIONS = ["OPEN", "IN_PROGRESS", "RESOLVED", "REJECTED"];
const ADMIN_RESPONSE_OPTIONS = [
    "PENDING_REVIEW",
    "UNDER_INVESTIGATION",
    "RESOLVED_REFUNDED",
    "RESOLVED_REPLACED",
    "RESOLVED_COMPENSATED",
    "REJECTED_INVALID",
    "REJECTED_POLICY",
    "ESCALATED"
];

const Eventusercoustomercare = () => {
    const [selectedStatusFilter, setSelectedStatusFilter] = useState("");
    const [selectedTicketTypeFilter, setSelectedTicketTypeFilter] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [showViewModal, setShowViewModal] = useState(false);
    const [userStats, setUserStats] = useState({});
    const [currentIssueForView, setCurrentIssueForView] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [viewLoading, setViewLoading] = useState(false);
    const [newStatus, setNewStatus] = useState("");
    const [adminResponse, setAdminResponse] = useState("");
    const [additionalNotes, setAdditionalNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [localAdminResponses, setLocalAdminResponses] = useState({});
    const [imagePreviewModal, setImagePreviewModal] = useState({ show: false, url: "" });

    // Handle body overflow when modals are open
    useEffect(() => {
        if (showViewModal || imagePreviewModal.show) {
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = '0px';
        } else {
            document.body.style.overflow = 'auto';
            document.body.style.paddingRight = '0px';
        }

        return () => {
            document.body.style.overflow = 'auto';
            document.body.style.paddingRight = '0px';
        };
    }, [showViewModal, imagePreviewModal.show]);

    const handleStatusChange = async (ticketId, newStatus, adminResponseToInclude = null) => {
        try {
            const token = localStorage.getItem("authToken");

            // Try to include adminResponse in the status update
            let url = `https://backend.maamaas.com/food/api/tickets/update-status/${ticketId}?status=${newStatus}`;

            if (adminResponseToInclude) {
                url += `&adminResponse=${encodeURIComponent(adminResponseToInclude)}`;
            }

            console.log("Updating status with URL:", url);

            const response = await fetch(url, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error("❌ API Error:", errorText);

                // Try without adminResponse if it fails
                if (adminResponseToInclude) {
                    console.log("Trying without adminResponse parameter...");
                    const simpleUrl = `https://backend.maamaas.com/food/api/tickets/update-status/${ticketId}?status=${newStatus}`;

                    const simpleResponse = await fetch(simpleUrl, {
                        method: "PUT",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    if (!simpleResponse.ok) {
                        const simpleError = await simpleResponse.text();
                        throw new Error(`Failed to update ticket status: ${simpleError}`);
                    }

                    const simpleResult = await simpleResponse.json();
                    console.log("✅ Status update successful (without adminResponse):", simpleResult);

                    // Store adminResponse locally since API doesn't support it
                    if (adminResponseToInclude) {
                        setLocalAdminResponses(prev => ({
                            ...prev,
                            [ticketId]: {
                                response: adminResponseToInclude,
                                notes: additionalNotes,
                                timestamp: new Date().toISOString()
                            }
                        }));
                    }

                    return {
                        success: true,
                        data: simpleResult,
                        adminResponseStoredLocally: !!adminResponseToInclude
                    };
                }

                throw new Error(`Failed to update ticket status: ${errorText}`);
            }

            const updatedIssue = await response.json();
            console.log("✅ Status update successful:", updatedIssue);

            setCurrentIssueForView((prev) => ({
                ...prev,
                status: updatedIssue.status || newStatus,
                adminResponse: updatedIssue.adminResponse || adminResponseToInclude || prev?.adminResponse
            }));

            return {
                success: true,
                data: updatedIssue,
                adminResponseStoredLocally: false
            };

        } catch (error) {
            console.error("Error updating status:", error);
            return {
                success: false,
                error: error.message
            };
        }
    };

    const storeAdminResponseLocally = (ticketId, response, notes = "") => {
        setLocalAdminResponses(prev => ({
            ...prev,
            [ticketId]: {
                response,
                notes,
                timestamp: new Date().toISOString(),
                adminName: localStorage.getItem("userName") || "Admin"
            }
        }));
        return true;
    };

    const getCombinedAdminResponse = (ticket) => {
        const localResponse = localAdminResponses[ticket.id];
        if (localResponse) {
            return {
                response: localResponse.response,
                notes: localResponse.notes,
                isLocal: true,
                timestamp: localResponse.timestamp,
                adminName: localResponse.adminName
            };
        }
        return {
            response: ticket.adminResponse,
            notes: "",
            isLocal: false,
            timestamp: ticket.resolvedAt || ticket.createdAt
        };
    };

    useEffect(() => {
        fetchTickets();
        // Load locally stored responses
        const savedResponses = localStorage.getItem("localAdminResponses");
        if (savedResponses) {
            setLocalAdminResponses(JSON.parse(savedResponses));
        }
    }, []);

    useEffect(() => {
        // Save locally stored responses
        localStorage.setItem("localAdminResponses", JSON.stringify(localAdminResponses));
    }, [localAdminResponses]);

    const fetchTickets = async () => {
        setLoading(true);
        setError("");

        try {
            const token = localStorage.getItem("authToken");
            const res = await fetch(
                "https://backend.maamaas.com/food/api/tickets/all",
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!res.ok) throw new Error("Failed to fetch tickets");

            const data = await res.json();
            console.log("🎫 Tickets fetched:", data);
            setTickets(data);

            // Calculate statistics
            const counts = {
                OPEN: 0,
                IN_PROGRESS: 0,
                RESOLVED: 0,
                REJECTED: 0,
                Total: 0
            };

            data.forEach(ticket => {
                const status = ticket.status || "UNKNOWN";
                if (STATUS_OPTIONS.includes(status)) {
                    counts[status] = (counts[status] || 0) + 1;
                }
            });
            counts.Total = data.length;

            setUserStats(counts);
        } catch (err) {
            setError(err.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const handleShowViewModal = async (ticket) => {
        setShowViewModal(true);
        setViewLoading(true);
        setAdminResponse("");
        setNewStatus("");
        setAdditionalNotes("");

        try {
            const token = localStorage.getItem("authToken");
            const response = await fetch(
                `https://backend.maamaas.com/food/api/tickets/${ticket.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch ticket details");
            }

            const data = await response.json();
            console.log("🎯 Ticket details:", data);
            setCurrentIssueForView(data);
        } catch (error) {
            console.error("❌ Error fetching ticket details:", error);
            setCurrentIssueForView(ticket);
        } finally {
            setViewLoading(false);
        }
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setCurrentIssueForView(null);
        setAdminResponse("");
        setNewStatus("");
        setAdditionalNotes("");
        setSaving(false);
    };

    const showImagePreview = (url) => {
        setImagePreviewModal({ show: true, url });
    };

    const closeImagePreview = () => {
        setImagePreviewModal({ show: false, url: "" });
    };

    const filteredTickets = useMemo(() => {
        return tickets.filter(ticket => {
            const matchesStatus = !selectedStatusFilter || ticket.status === selectedStatusFilter;
            const matchesType = !selectedTicketTypeFilter || ticket.ticketType === selectedTicketTypeFilter;
            const matchesSearch = !searchTerm ||
                (ticket.message && ticket.message.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (ticket.id && ticket.id.toString().includes(searchTerm)) ||
                (ticket.userId && ticket.userId.toString().includes(searchTerm)) ||
                (ticket.orderId && ticket.orderId.toString().includes(searchTerm));

            return matchesStatus && matchesType && matchesSearch;
        }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }, [tickets, selectedStatusFilter, selectedTicketTypeFilter, searchTerm]);

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case "OPEN":
                return "bg-warning text-dark";
            case "IN_PROGRESS":
                return "bg-info text-dark";
            case "RESOLVED":
                return "bg-success text-white";
            case "REJECTED":
                return "bg-danger text-white";
            default:
                return "bg-secondary text-white";
        }
    };

    const getTicketTypeBadgeClass = (ticketType) => {
        switch (ticketType) {
            case "SERVICE_QUALITY":
                return "bg-primary text-white";
            case "FOOD_QUALITY":
                return "bg-success text-white";
            case "DELIVERY_ISSUE":
                return "bg-warning text-dark";
            case "PAYMENT_PROBLEM":
                return "bg-danger text-white";
            case "OTHER":
                return "bg-secondary text-white";
            default:
                return "bg-dark text-white";
        }
    };

    const getAdminResponseBadgeClass = (response) => {
        if (!response) return "bg-light text-dark";

        if (response.includes("RESOLVED")) return "bg-success text-white";
        if (response.includes("REJECTED")) return "bg-danger text-white";
        if (response.includes("PENDING") || response.includes("UNDER_INVESTIGATION")) return "bg-warning text-dark";
        if (response === "ESCALATED") return "bg-info text-dark";

        return "bg-secondary text-white";
    };

    const formatTicketType = (ticketType) => {
        return ticketType ? ticketType.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : "Unknown";
    };

    const formatAdminResponse = (response) => {
        if (!response) return "No Response";
        return response.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const handleSaveChanges = async () => {
        if (!newStatus && !adminResponse) {
            alert("Please update status or add a response");
            return;
        }

        setSaving(true);
        let messages = [];

        try {
            // Handle status update (with or without adminResponse)
            if (currentIssueForView) {
                const result = await handleStatusChange(
                    currentIssueForView.id,
                    newStatus || currentIssueForView.status,
                    adminResponse || undefined
                );

                if (result.success) {
                    if (newStatus) {
                        messages.push(`Status updated to ${newStatus}`);
                    }

                    if (adminResponse && result.adminResponseStoredLocally) {
                        storeAdminResponseLocally(currentIssueForView.id, adminResponse, additionalNotes);
                        messages.push(`Admin response "${formatAdminResponse(adminResponse)}" stored locally (API doesn't support adminResponse field)`);
                    } else if (adminResponse) {
                        messages.push(`Admin response updated to ${formatAdminResponse(adminResponse)}`);
                    }

                    if (additionalNotes.trim() && !result.adminResponseStoredLocally) {
                        messages.push("Additional notes added");
                    }
                } else {
                    messages.push(`Failed to update: ${result.error}`);
                }
            }

            // Refresh tickets
            await fetchTickets();

            // Show success message
            if (messages.length > 0) {
                alert("✅ " + messages.join("\n"));
            }

            handleCloseViewModal();

        } catch (error) {
            console.error("Error saving changes:", error);
            alert("Failed to save changes: " + error.message);
        } finally {
            setSaving(false);
        }
    };

    const renderFilterControls = () => (
        <div className="row mb-3">



        </div>
    );

    return (
        <div className="container-fluid mt-4 mb-5">
            {/* Stats Dashboard Row */}
            <div className="row row-cols-2 row-cols-sm-3 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 justify-content-start text-center mb-4 gy-3 p-3">
                {STATUS_OPTIONS.map((status) => (
                    <div className="col" key={`stat-${status}`}>
                        <div
                            className={`card h-100 text-white ${getStatusBadgeClass(status).split(" ")[0]}`}
                        >
                            <div className="card-body p-2">
                                <h6 className="card-title mb-0">{userStats[status] || 0}</h6>
                                <small className="card-text">{status}</small>
                            </div>
                        </div>
                    </div>
                ))}
                <div className="col">
                    <div
                        className="card h-100 text-white"
                        style={{ backgroundColor: "rgb(153, 0, 255)" }}
                    >
                        <div className="card-body p-2">
                            <h6 className="card-title mb-0">{userStats.Total || 0}</h6>
                            <small className="card-text">Total Tickets</small>
                        </div>
                    </div>
                </div>
            </div>

            {/* Warning about local storage if we have local responses */}
            {Object.keys(localAdminResponses).length > 0 && (
                <div className="alert alert-info mb-3">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Note:</strong> Some admin responses are stored locally because the API doesn't support the adminResponse field. These will be preserved in your browser's local storage.
                </div>
            )}

            {/* Filter Controls */}
            {renderFilterControls()}

            {/* Issues Table */}
            <div className="table-responsive shadow-sm rounded">
                <table className="table table-hover table-bordered align-middle caption-top">
                    <thead style={{ fontSize: "0.9rem", verticalAlign: "middle" }}>
                        <tr>
                            <th style={tableHeaderStyle} className="p-2">Ticket ID</th>
                            <th style={tableHeaderStyle} className="p-2">User ID</th>
                            <th style={tableHeaderStyle} className="p-2">Order ID</th>
                            <th style={tableHeaderStyle} className="p-2">Type</th>
                            <th style={{ ...tableHeaderStyle, minWidth: "200px" }} className="p-2">Message</th>
                            <th style={tableHeaderStyle} className="p-2">Status</th>
                            <th style={tableHeaderStyle} className="p-2">Reported Date</th>
                            <th style={tableHeaderStyle} className="p-2">Reported Time</th>
                            <th style={tableHeaderStyle} className="p-2">Resolved At</th>
                            <th style={tableHeaderStyle} className="p-2">Admin Response</th>
                            <th style={tableHeaderStyle} className="p-2">Image</th>
                            <th style={{ ...tableHeaderStyle, minWidth: "130px" }} className="p-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="12" className="text-center p-4">Loading tickets...</td>
                            </tr>
                        ) : error ? (
                            <tr>
                                <td colSpan="12" className="text-center p-4 text-danger">{error}</td>
                            </tr>
                        ) : filteredTickets.length > 0 ? (
                            filteredTickets.map((ticket) => {
                                const combinedResponse = getCombinedAdminResponse(ticket);

                                return (
                                    <tr key={ticket.id} style={{ fontSize: "0.9rem" }}>
                                        <td style={tableCellStyle} className="p-2">{ticket.id}</td>
                                        <td style={tableCellStyle} className="p-2">
                                            {ticket.userId ? (
                                                <Link to={`/user-profile/${ticket.userId}`} className="text-decoration-none">
                                                    {ticket.userId}
                                                </Link>
                                            ) : "N/A"}
                                        </td>
                                        <td style={tableCellStyle} className="p-2">
                                            {ticket.orderId ? (
                                                <Link to={`/order-details/${ticket.orderId}`} className="text-decoration-none">
                                                    {ticket.orderId}
                                                </Link>
                                            ) : "N/A"}
                                        </td>
                                        <td style={tableCellStyle} className="p-2">
                                            <span className={`badge rounded-pill ${getTicketTypeBadgeClass(ticket.ticketType)}`}>
                                                {formatTicketType(ticket.ticketType)}
                                            </span>
                                        </td>
                                        <td style={{ ...tableCellStyle }} className="text-break p-2">
                                            {ticket.message || "No message"}
                                        </td>
                                        <td style={tableCellStyle} className="p-2">
                                            <span className={`badge rounded-pill ${getStatusBadgeClass(ticket.status)}`}>
                                                {ticket.status || "UNKNOWN"}
                                            </span>
                                        </td>
                                        <td style={tableCellStyle} className="p-2">
                                            {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString() : "N/A"}
                                        </td>
                                        <td style={tableCellStyle} className="p-2">
                                            {ticket.createdAt ? new Date(ticket.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "N/A"}
                                        </td>
                                        <td style={tableCellStyle} className="p-2">
                                            {ticket.resolvedAt ? new Date(ticket.resolvedAt).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td style={tableCellStyle} className="p-2">
                                            <div>
                                                <span className={`badge rounded-pill ${getAdminResponseBadgeClass(combinedResponse.response)}`}>
                                                    {formatAdminResponse(combinedResponse.response)}
                                                    {combinedResponse.isLocal && (
                                                        <span className="ms-1" title="Stored locally">
                                                            <i className="bi bi-device-ssd"></i>
                                                        </span>
                                                    )}
                                                </span>
                                                {combinedResponse.isLocal && (
                                                    <small className="d-block text-muted mt-1">
                                                        Local: {new Date(combinedResponse.timestamp).toLocaleDateString()}
                                                    </small>
                                                )}
                                            </div>
                                        </td>
                                        <td style={tableCellStyle} className="p-2">
                                            {ticket.attachmentUrl ? (
                                                <div className="image-thumbnail-container" style={{ width: "80px", height: "60px" }}>
                                                    <img
                                                        src={ticket.attachmentUrl}
                                                        alt="Ticket attachment"
                                                        className="img-thumbnail cursor-pointer"
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                            cursor: "pointer"
                                                        }}
                                                        onClick={() => showImagePreview(ticket.attachmentUrl)}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = 'https://via.placeholder.com/80x60?text=No+Image';
                                                            e.target.style.objectFit = "contain";
                                                            e.target.style.padding = "5px";
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <span className="text-muted">No Image</span>
                                            )}
                                        </td>
                                        <td style={tableCellStyle} className="p-2">
                                            <div className="btn-group btn-group-sm" role="group">
                                                <button
                                                    className="btn btn-outline-primary"
                                                    onClick={() => handleShowViewModal(ticket)}
                                                >
                                                    View
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="12" className="text-center p-4">
                                    <h5 className="text-muted">No tickets found.</h5>
                                    <p className="text-muted">
                                        {tickets.length === 0
                                            ? "No support tickets have been created yet."
                                            : "No tickets match your filters."}
                                    </p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Image Preview Modal */}
            {imagePreviewModal.show && (
                <div className="modal-overlay-fixed" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div className="modal-dialog modal-dialog-centered modal-xl" style={{
                        zIndex: 10000,
                        width: '100%',
                        maxWidth: '1200px'
                    }}>
                        <div className="modal-content bg-transparent border-0 shadow-lg">
                            <div className="modal-header border-0">
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={closeImagePreview}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <div className="modal-body text-center">
                                <img
                                    src={imagePreviewModal.url}
                                    alt="Full size preview"
                                    className="img-fluid rounded"
                                    style={{
                                        maxHeight: '80vh',
                                        maxWidth: '100%',
                                        boxShadow: '0 5px 20px rgba(0,0,0,0.3)'
                                    }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
                                    }}
                                />
                                <div className="mt-4">
                                    <a
                                        href={imagePreviewModal.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-primary me-3 px-4 py-2"
                                        style={{ borderRadius: '8px' }}
                                    >
                                        <i className="bi bi-box-arrow-up-right me-2"></i>Open in New Tab
                                    </a>
                                    <a
                                        href={imagePreviewModal.url}
                                        download
                                        className="btn btn-secondary px-4 py-2"
                                        style={{ borderRadius: '8px' }}
                                    >
                                        <i className="bi bi-download me-2"></i>Download
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View Details Modal */}
            {/* View Details Modal */}
            {/* View Details Modal */}
            {showViewModal && (
                <div className="modal-overlay-fixed" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    zIndex: 9998,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div className="modal-dialog modal-dialog-centered" style={{
                        zIndex: 9999,
                        width: '100%',
                        maxWidth: '600px',  // Reduced further
                        margin: '0 auto'
                    }}>
                        <div className="modal-content" style={{
                            borderRadius: '10px',
                            boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                            border: 'none',
                            maxHeight: '85vh',  // Limit overall height
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Modal Header */}
                            <div className="modal-header text-white" style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderBottom: 'none',
                                padding: '1rem 1.2rem',
                                flexShrink: 0
                            }}>
                                <h5 className="modal-title fw-bold mb-0" style={{ fontSize: '1rem' }}>
                                    <i className="bi bi-ticket-detailed me-1"></i>
                                    {viewLoading ? "Loading Ticket..." : `Ticket #${currentIssueForView?.id || "N/A"}`}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={handleCloseViewModal}
                                    aria-label="Close"
                                    style={{
                                        filter: 'brightness(0) invert(1)',
                                        fontSize: '0.7rem'
                                    }}
                                ></button>
                            </div>

                            <div className="modal-body" style={{
                                padding: '1.2rem',
                                backgroundColor: '#f8f9fa',
                                overflowY: 'auto',
                                flex: 1
                            }}>
                                {viewLoading ? (
                                    <div className="text-center py-3">
                                        <div className="spinner-border text-primary" style={{ width: '2rem', height: '2rem' }} role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.9rem' }}>Fetching ticket details...</p>
                                    </div>
                                ) : currentIssueForView ? (
                                    <>
                                        {/* Compact Ticket Info Row */}
                                        <div className="row mb-3">
                                            <div className="col-6 mb-2">
                                                <div className="p-2 bg-light rounded border">
                                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Ticket ID</small>
                                                    <strong className="d-block" style={{ fontSize: '0.85rem' }}>{currentIssueForView.id}</strong>
                                                </div>
                                            </div>
                                            <div className="col-6 mb-2">
                                                <div className="p-2 bg-light rounded border">
                                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Type</small>
                                                    <span className={`badge ${getTicketTypeBadgeClass(currentIssueForView.ticketType)} px-2 py-1`} style={{ fontSize: '0.75rem' }}>
                                                        {formatTicketType(currentIssueForView.ticketType)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="col-6 mb-2">
                                                <div className="p-2 bg-light rounded border">
                                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>User ID</small>
                                                    <strong className="d-block" style={{ fontSize: '0.85rem' }}>
                                                        {currentIssueForView.userId ? (
                                                            <Link to={`/user-profile/${currentIssueForView.userId}`} className="text-decoration-none text-primary">
                                                                {currentIssueForView.userId}
                                                            </Link>
                                                        ) : "N/A"}
                                                    </strong>
                                                </div>
                                            </div>
                                            <div className="col-6 mb-2">
                                                <div className="p-2 bg-light rounded border">
                                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Order ID</small>
                                                    <strong className="d-block" style={{ fontSize: '0.85rem' }}>
                                                        {currentIssueForView.orderId ? (
                                                            <Link to={`/order-details/${currentIssueForView.orderId}`} className="text-decoration-none text-primary">
                                                                {currentIssueForView.orderId}
                                                            </Link>
                                                        ) : "N/A"}
                                                    </strong>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Timestamps */}
                                        {/* Timestamps */}
                                        <div className="row mb-3">
                                            <div className="col-6">
                                                <div className="p-2 bg-light rounded border">
                                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Created</small>
                                                    <div className="d-flex align-items-center">
                                                        <i className="bi bi-calendar-plus me-1" style={{ fontSize: '0.8rem' }}></i>
                                                        <span style={{ fontSize: '0.8rem', marginLeft: "75px" }}>
                                                            {currentIssueForView.createdAt ?
                                                                `${new Date(currentIssueForView.createdAt).getDate().toString().padStart(2, '0')}/${(new Date(currentIssueForView.createdAt).getMonth() + 1).toString().padStart(2, '0')}/${new Date(currentIssueForView.createdAt).getFullYear()}`
                                                                : 'N/A'}
                                                        </span>
                                                    </div>
                                                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                        {currentIssueForView.createdAt ?
                                                            `${new Date(currentIssueForView.createdAt).getHours().toString().padStart(2, '0')}:${new Date(currentIssueForView.createdAt).getMinutes().toString().padStart(2, '0')}`
                                                            : ''}
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="p-2 bg-light rounded border">
                                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Resolved</small>
                                                    <div className="d-flex align-items-center">
                                                        <i className="bi bi-check-circle me-1" style={{ fontSize: '0.8rem' }}></i>
                                                        <span style={{ fontSize: '0.8rem', marginLeft: "75px" }}>
                                                            {currentIssueForView.resolvedAt ?
                                                                `${new Date(currentIssueForView.resolvedAt).getDate().toString().padStart(2, '0')}/${(new Date(currentIssueForView.resolvedAt).getMonth() + 1).toString().padStart(2, '0')}/${new Date(currentIssueForView.resolvedAt).getFullYear()}`
                                                                : 'Not resolved'}
                                                        </span>
                                                    </div>
                                                    {currentIssueForView.resolvedAt && (
                                                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                            {`${new Date(currentIssueForView.resolvedAt).getHours().toString().padStart(2, '0')}:${new Date(currentIssueForView.resolvedAt).getMinutes().toString().padStart(2, '0')}`}
                                                        </small>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Issue Description */}
                                        <div className="mb-3">
                                            <h6 className="fw-bold text-primary mb-2" style={{ fontSize: '0.9rem' }}>
                                                <i className="bi bi-chat-left-text me-1"></i>
                                                Issue Description
                                            </h6>
                                            <div className="p-3 bg-light rounded border">
                                                <p className="mb-0" style={{ fontSize: '0.9rem', lineHeight: '1.4' }}>
                                                    {currentIssueForView.message || "No description provided"}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Current Admin Response */}
                                        <div className="mb-3">
                                            <h6 className="fw-bold text-primary mb-2" style={{ fontSize: '0.9rem' }}>
                                                <i className="bi bi-person-badge me-1"></i>
                                                Current Response
                                            </h6>
                                            <div className="text-center">
                                                {currentIssueForView.adminResponse || localAdminResponses[currentIssueForView.id] ? (
                                                    <div>
                                                        <span className={`badge ${getAdminResponseBadgeClass(
                                                            localAdminResponses[currentIssueForView.id]?.response ||
                                                            currentIssueForView.adminResponse
                                                        )} px-3 py-2 mb-2`} style={{
                                                            fontSize: '0.8rem'
                                                        }}>
                                                            {formatAdminResponse(
                                                                localAdminResponses[currentIssueForView.id]?.response ||
                                                                currentIssueForView.adminResponse
                                                            )}
                                                            {localAdminResponses[currentIssueForView.id] && (
                                                                <span className="ms-1" title="Stored locally">
                                                                    <i className="bi bi-device-ssd"></i>
                                                                </span>
                                                            )}
                                                        </span>
                                                        {localAdminResponses[currentIssueForView.id]?.notes && (
                                                            <div className="alert alert-info p-2 mt-2 mb-2" style={{ fontSize: '0.8rem' }}>
                                                                <strong>Notes:</strong> {localAdminResponses[currentIssueForView.id].notes}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="text-muted p-3 bg-light rounded">
                                                        <i className="bi bi-chat-square-text me-1"></i>
                                                        No response yet
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Image Attachment - Only show if exists */}
                                        {currentIssueForView.attachmentUrl && (
                                            <div className="mb-3">
                                                <h6 className="fw-bold text-primary mb-2" style={{ fontSize: '0.9rem' }}>
                                                    <i className="bi bi-image me-1"></i>
                                                    Attachment
                                                </h6>
                                                <div className="text-center">
                                                    <div className="mb-2">
                                                        <img
                                                            src={currentIssueForView.attachmentUrl}
                                                            alt="Ticket attachment"
                                                            className="img-fluid rounded border"
                                                            style={{
                                                                maxHeight: '120px',
                                                                maxWidth: '100%'
                                                            }}
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = 'https://via.placeholder.com/200x120?text=Image';
                                                            }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <a
                                                            href={currentIssueForView.attachmentUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-primary btn-sm me-1"
                                                            style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}
                                                        >
                                                            <i className="bi bi-box-arrow-up-right me-1"></i>Open
                                                        </a>
                                                        <a
                                                            href={currentIssueForView.attachmentUrl}
                                                            download
                                                            className="btn btn-outline-secondary btn-sm"
                                                            style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}
                                                        >
                                                            <i className="bi bi-download me-1"></i>Download
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Update Section - Single Row */}
                                        <div className="row mt-3">
                                            <div className="col-6">
                                                <h6 className="fw-bold text-primary mb-2" style={{ fontSize: '0.9rem' }}>
                                                    <i className="bi bi-arrow-clockwise me-1"></i>
                                                    Status
                                                </h6>
                                                <select
                                                    className="form-select form-select-sm mb-2"
                                                    value={newStatus}
                                                    onChange={(e) => setNewStatus(e.target.value)}
                                                    style={{ fontSize: '0.85rem' }}
                                                >
                                                    <option value="">Select status</option>
                                                    {STATUS_OPTIONS.map(status => (
                                                        <option key={status} value={status}>{status}</option>
                                                    ))}
                                                </select>
                                                <div className="d-flex align-items-center">
                                                    <small className="text-muted me-2" style={{ fontSize: '0.8rem' }}>Current:</small>
                                                    <span className={`badge ${getStatusBadgeClass(currentIssueForView.status)}`} style={{ fontSize: '0.75rem' }}>
                                                        {currentIssueForView.status}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <h6 className="fw-bold text-primary mb-2" style={{ fontSize: '0.9rem' }}>
                                                    <i className="bi bi-reply me-1"></i>
                                                    Response
                                                </h6>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={adminResponse}
                                                    onChange={(e) => setAdminResponse(e.target.value)}
                                                    style={{ fontSize: '0.85rem' }}
                                                >
                                                    <option value="">Select response</option>
                                                    {ADMIN_RESPONSE_OPTIONS.map(response => (
                                                        <option key={response} value={response}>{formatAdminResponse(response)}</option>
                                                    ))}
                                                </select>
                                                {adminResponse && (
                                                    <small className="text-warning d-block mt-1" style={{ fontSize: '0.7rem' }}>
                                                        <i className="bi bi-info-circle me-1"></i>
                                                        Stored locally
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center py-4">
                                        <i className="bi bi-exclamation-triangle text-danger mb-2" style={{ fontSize: '1.5rem' }}></i>
                                        <p className="text-danger mb-0" style={{ fontSize: '0.9rem' }}>Failed to load ticket details</p>
                                    </div>
                                )}
                            </div>

                            {/* Modal Footer - Compact */}
                            <div className="modal-footer border-top-0 py-2 px-3" style={{
                                background: '#f8f9fa',
                                flexShrink: 0,
                                gap: "30px"
                            }}>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={handleCloseViewModal}
                                    disabled={saving}
                                    style={{
                                        fontSize: '0.85rem',
                                        padding: '0.375rem 0.75rem'
                                    }}
                                >
                                    <i className="bi bi-x-lg me-1"></i>Close
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={handleSaveChanges}
                                    disabled={(!newStatus && !adminResponse) || saving}
                                    style={{
                                        fontSize: '0.85rem',
                                        padding: '0.375rem 0.75rem'
                                    }}
                                >
                                    {saving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                            Saving
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-save me-1"></i>Save
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const tableHeaderStyle = {
    border: '1px solid #ddd',
    textAlign: 'center',
    fontWeight: 'bold',
    backgroundColor: '#b15cd6',
    color: 'white',
    minWidth: '95px'
};

const tableCellStyle = {
    border: '1px solid #ddd',
    textAlign: 'center',
    verticalAlign: 'middle'
};

export default Eventusercoustomercare;